import { ReactEventHandler, useContext, useMemo } from 'react';

import { useRouteChangePrompt } from '../../hooks/useRouteChangePrompt';
import { SaveButton, SaveButtonStatus } from '../Button/SaveButton';

import { ActionBar } from './ActionBar';
import { SaveBarContainerContext } from './SaveBarProvider';

import { Box, Button, Spacer } from '@aircall/tractor';
import { typedMemo } from '@dashboard/library';
import { useSaveStatusHook } from '@hooks/useSaveStatusHook/useSaveStatusHook';
import { AnimatePresence, motion, Variants } from 'framer-motion';
import { createPortal } from 'react-dom';
import { FormRenderProps } from 'react-final-form';
import { useTranslation } from 'react-i18next';

const ActionBarWithMotion = motion(Box);
const animationVariants: Variants = {
  enter: {
    opacity: 1,
    height: 65,
    transition: {
      duration: 0.5,
      ease: 'easeInOut',
    },
  },
  exit: {
    opacity: 0,
    height: '0rem',
    transition: {
      duration: 0.35,
      ease: 'easeInOut',
    },
  },
};

export interface SaveBarProps<FormValues, InitialFormValues> {
  /** Whether we should show the prompt when route change is detected */
  shouldBlockNavigation?: boolean;

  /** Form props form react-final-form */
  formProps: FormRenderProps<FormValues, InitialFormValues>;
}

export const InnerSaveBar = typedMemo(
  <FormValues, InitialFormValues>({
    shouldBlockNavigation = false,
    formProps,
  }: SaveBarProps<FormValues, InitialFormValues>): JSX.Element => {
    const {
      hasValidationErrors: hasErrors,
      pristine,
      submitting,
      handleSubmit: onSubmit,
      submitSucceeded,
      submitFailed,
    } = formProps;
    const onReset = formProps.form.reset;
    const { t } = useTranslation();

    useRouteChangePrompt(
      // only block the navigation if the form values are changed
      shouldBlockNavigation && !pristine
    );

    const savingStatus = useMemo(() => {
      if (submitting) {
        return SaveButtonStatus.SAVING;
      }
      if (submitSucceeded && pristine) {
        return SaveButtonStatus.SAVED;
      }
      return SaveButtonStatus.UNSAVED;
    }, [pristine, submitSucceeded, submitting]);

    const visibleAfterSaved = useSaveStatusHook(savingStatus === SaveButtonStatus.SAVED);
    const isSaving = savingStatus === SaveButtonStatus.SAVING;

    const shouldShowSavebar =
      !pristine || isSaving || (submitSucceeded && visibleAfterSaved) || submitFailed;

    return (
      <AnimatePresence>
        {/* should not display the save bar if the user hasn't edit anything */}
        {shouldShowSavebar && (
          <ActionBarWithMotion
            data-test='save-bar'
            variants={animationVariants}
            initial='exit'
            animate='enter'
            exit='exit'
          >
            <ActionBar>
              <Spacer space='xxs' alignItems='center'>
                {onReset && savingStatus === 'unsaved' && (
                  <Button
                    onClick={onReset as unknown as ReactEventHandler<Element>}
                    variant='black'
                    mode='link'
                    data-test='undo-button'
                  >
                    {t('app.form.buttons.undo_all')}
                  </Button>
                )}
                <SaveButton status={savingStatus} onClick={onSubmit} disabled={hasErrors} />
              </Spacer>
            </ActionBar>
          </ActionBarWithMotion>
        )}
      </AnimatePresence>
    );
  }
);

interface SaveBarContainerProps<FormValues, InitialFormValues>
  extends SaveBarProps<FormValues, InitialFormValues> {
  shouldUseContext?: boolean;
}
/**
 * The save action bar with the save button and undo button.
 * It will display the status of the saving progress based on the status of the form: `pristine` and `submitting`.
 * Also, it will show a modal to let users know about there is unsaved changes before leaving the page,
 * if `shouldBlockNavigation` is true. <br>
 * You can use it along with
 * `<SaveBarProvider portalContainerRef={containerRef}>`...`<Footer ref={containerRef} /></SaveBarProvider>`
 * then `<SaveBar>` will appear inside the container ref points to (`<Footer>` in this case).
 */
export const SaveBar = typedMemo(
  <FormValues, InitialFormValues>({
    shouldUseContext = true,
    formProps,
    ...restProps
  }: SaveBarContainerProps<FormValues, InitialFormValues>) => {
    const { containerElement } = useContext(SaveBarContainerContext);
    // if the document has the element to put the save bar,
    // use the portal to push the element
    if (shouldUseContext && containerElement) {
      return createPortal(<InnerSaveBar formProps={formProps} />, containerElement);
    }

    // else, render the normal save bar
    return <InnerSaveBar formProps={formProps} {...restProps} />;
  }
);
